import { Component, EventEmitter, OnInit } from '@angular/core';
import { ECharts, EChartsOption } from 'echarts';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsHelper } from 'src/app/utils/echarts/echarts-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { IMultiSelectSettingDashboardItem } from '../common/items/setting/multi-select/multi-select.component';
import { ISingleSelectSettingDashboardItem } from '../common/items/setting/single-select/single-select.component';
import { BiVizDashboardRetention } from '../common/retention/sql-maps/biviz-dashboard-retention-sql-map';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { MopaRetentionChartDocs } from './mopa-retention.chart-documentation';
import { datePartDateTrunc, getAllDatePartDateTrunc } from 'src/app/utils/sql';

@Component({
    selector: 'app-mopa-retention',
    templateUrl: './mopa-retention.component.html',
    styleUrls: ['./mopa-retention.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class MopaRetentionComponent
    extends StandardDashboardWorker
    implements OnInit
{
    override DASHBOARD_NAME = 'Retention';
    override DASHBOARD_ID = 'mopa-retention';
    keys = Object.keys;
    entries = Object.entries;
    values = Object.values;

    private gcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'mopa.retention.modify_time_retention_datasource': DataLoaderFactories[
            'common.modify_time'
        ]({
            createdInstanceId:
                'mopa.retention.modify_time_retention_datasource',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasetId: 'bi_viz',
            tableId: 'dashboard_retention',
        }).create(),
        'mopa.retention.retention_by_day_no': DataLoaderFactories[
            'common.retention.retention_by_day_no'
        ]({
            createdInstanceId: 'mopa.retention.retention_by_day_no',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
        }).create(),
        'mopa.retention.retention_by_first_open_date': DataLoaderFactories[
            'common.retention.retention_by_first_open_date'
        ]({
            createdInstanceId: 'mopa.retention.retention_by_first_open_date',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            retentionType: 'retention',
        }).create(),
        'mopa.retention.rolling_retention_by_first_open_date':
            DataLoaderFactories[
                'common.retention.retention_by_first_open_date'
            ]({
                createdInstanceId:
                    'mopa.retention.rolling_retention_by_first_open_date',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                retentionType: 'rolling_retention',
            }).create(),
    };

    override filterDataLoaders = {
        // 'SEGMENTS':
        'mopa.retention.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ](
            new SegmentDatasourceColumnDataLoaderFactoryOptions(
                'mopa.retention.filter_segments',
                this.gcpProjectId,
                this.queryCache
            )
        ).create(),
        // 'FIREBASE_APP_STORE':
        'mopa.retention.filter.firebase_app_store': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.retention.filter.firebase_app_store',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_APP_STORE',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
        }).create(),
        // 'FIREBASE_COUNTRY_EUROPEAN_UNION':
        'mopa.retention.filter.firebase_country_european_union':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.retention.filter.firebase_country_european_union',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                datasourceSqlMap: BiVizDashboardRetention.sqlMap,
            }).create(),
        // 'FIREBASE_COUNTRY_TIER':
        'mopa.retention.filter.firebase_country_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.retention.filter.firebase_country_tier',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
        }).create(),
        // 'FIREBASE_COUNTRY':
        'mopa.retention.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.retention.filter.firebase_country',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
        }).create(),
        // 'FIREBASE_COUNTRY':
        'mopa.retention.filter.firebase_country_us_region': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.retention.filter.firebase_country_us_region',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_US_REGION',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
        }).create(),
        // 'ATTRIBUTION_TRAFFIC_SOURCE':
        'mopa.retention.filter.attribution_traffic_source': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.retention.filter.attribution_traffic_source',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
        }).create(),
        // 'FIREBASE_INSTALL_VERSION':
        'mopa.retention.filter.firebase_install_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.retention.filter.firebase_install_version',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_INSTALL_VERSION',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery(
                'FIREBASE_INSTALL_VERSION'
            ),
        }).create(),
        'mopa.retention.filter.firebase_install_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.retention.filter.firebase_install_version_group_minor',
                gcpProjectId: this.gcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIREBASE_INSTALL_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardRetention.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                ),
            }).create(),
        // 'ETL_PAYER':
        'mopa.retention.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.retention.filter.etl_payer',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_PAYER',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
        }).create(),
        // 'ETL_REVENUE_BADGE':
        'mopa.retention.filter.etl_revenue_badge': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.retention.filter.etl_revenue_badge',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ETL_REVENUE_BADGE',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('ETL_REVENUE_BADGE'),
        }).create(),
        // 'ATTRIBUTION_NETWORK_NAME':
        'mopa.retention.filter.attribution_network_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.retention.filter.attribution_network_name',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
        }).create(),
        // 'ATTRIBUTION_CAMPAIGN_NAME':
        'mopa.retention.filter.attribution_campaign_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.retention.filter.attribution_campaign_name',
            gcpProjectId: this.gcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
            datasourceSqlMap: BiVizDashboardRetention.sqlMap,
            filterContext: true,
        }).create(),
    };

    // spread nie sklonuje obiektów DataLoader, tylko przepisze ich referencje
    allDataLoaders = { ...this.chartDataLoaders, ...this.filterDataLoaders };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////

    settingsCards = {
        retentionMaxDayNo: {
            options: {
                id: QueryContextSettingID.RETENTION_MAX_DAY_NO,
                viewtype: 'singleSelect',
                displayName: 'Limit max day no',
                value: this.getInitialContext().settings[
                    QueryContextSettingID.RETENTION_MAX_DAY_NO
                ],
                selectOptions: ['60', '90', '180', '360', '720', 'unlimited'],
            },
        } as ISingleSelectSettingDashboardItem,
        retentionSelectedDayNo: {
            options: {
                id: QueryContextSettingID.RETENTION_SELECTED_DAY_NO,
                viewtype: 'multiSelect',
                displayName: 'Selected day no',
                value: this.getInitialContext().settings[
                    QueryContextSettingID.RETENTION_SELECTED_DAY_NO
                ],
                selectOptions: [
                    '1',
                    '3',
                    '7',
                    '14',
                    '30',
                    '90',
                    '180',
                    '360',
                ].map((v) => ({ value: v, label: v })),
            },
        } as IMultiSelectSettingDashboardItem,
        dateAggregation: {
            options: {
                id: QueryContextSettingID.DATE_AGGREGATION,
                viewtype: 'singleSelect',
                displayName: 'Date aggregation',
                value: 'day' as datePartDateTrunc,
                selectOptions: getAllDatePartDateTrunc(),
            },
        } as ISingleSelectSettingDashboardItem,
    };

    // CHARTS
    modify_time_retention_datasource: IDashboardItemDataLoaderLink = {
        id: 'mopa.retention.modify_time_retention_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'mopa.retention.modify_time_retention_datasource',
                'value'
            )
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.retention.modify_time_retention_datasource'
            ].emitter,
    };
    retention_per_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.retention.chart.retention_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.retention.chart.retention_by_day_no',
            dashboardItem: {
                title: 'Retention by day_no',
                chartDocumentation: MopaRetentionChartDocs.retention_per_day_no,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'retention',
            metricName: 'Retention',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) => `Day no: ${label}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.retention.retention_by_day_no'].emitter,
    };
    rolling_retention_per_day_no: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.retention.chart.rolling_retention_by_day_no',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.retention.chart.rolling_retention_by_day_no',

            dashboardItem: {
                title: 'Rolling retention by day_no',
                chartDocumentation:
                    MopaRetentionChartDocs.rolling_retention_per_day_no,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'rolling_retention',
            metricName: 'Rolling retention',
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) => `Day no: ${label}`,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.retention.retention_by_day_no'].emitter,
    };
    retention_by_first_open_date: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.retention.chart.retention_by_first_open_date',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.retention.chart.retention_per_day_no',
            dashboardItem: {
                title: 'Retention by first open date',
                chartDocumentation:
                    MopaRetentionChartDocs.retention_by_first_open_date,
            },
            xColumnLabel: 'date',
            metricsGetter: EChartsHelper.createRetentionMetricsGetter({
                metricId: 'retention',
                metricName: 'Retention',
            }),
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            EChartsAxisHelper.axisLabelDateFormatter(
                                label,
                                context
                            ),
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.retention.retention_by_first_open_date']
                .emitter,
    };
    rolling_retention_by_first_open_date: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.retention.chart.rolling_retention_by_first_open_date',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId:
                'mopa.retention.chart.rolling_retention_by_first_open_date',
            dashboardItem: {
                title: 'Rolling retention by first open date',
                chartDocumentation:
                    MopaRetentionChartDocs.rolling_retention_by_first_open_date,
            },
            xColumnLabel: 'date',
            yColumnLabel: 'retention',
            metricName: 'Rolling retention',
            metricsGetter: EChartsHelper.createRetentionMetricsGetter({
                metricId: 'retention',
                metricName: 'Rolling retention',
            }),
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter:
                            EChartsAxisHelper.getAxisPercentLabelFormatter(0),
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) =>
                            EChartsAxisHelper.axisLabelDateFormatter(
                                label,
                                context
                            ),
                        valueFormatter:
                            EChartsTooltipHelper.tooltipPercentValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.retention.rolling_retention_by_first_open_date'
            ].emitter,
    };
    avg_user_lifetime: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.retention.chart.avg_user_lifetime',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'mopa.retention.chart.avg_user_lifetime',
            dashboardItem: {
                title: "Average users's lifetime",
                chartDocumentation: MopaRetentionChartDocs.avg_user_lifetime,
            },
            xColumnLabel: 'day_no',
            yColumnLabel: 'avg_user_lifetime',
            metricName: "Avg users's lifetime",
            echartsSeriesType: 'line',
            xAxisType: 'value',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartsInstance: ECharts
            ) => {
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartsInstance, {
                        xLabelFormatter: (label: any) => `Day no: ${label}`,
                        valueFormatter: (v: any) =>
                            EChartsTooltipHelper.tooltipNumberValueFormatter(
                                v,
                                ' days'
                            ),
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.retention.retention_by_day_no'].emitter,
    };

    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        SEGMENT: {
            id: 'mopa.retention.filter.segments',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'mopa.retention.filter.segments',
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.retention.filter.segment'].emitter,
        },
        FIREBASE_FIRST_OPEN_DATE: {
            id: 'mopa.retention.filter.firebase_first_open_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'mopa.retention.filter.firebase_first_open_date',
                datasourceColumnId: 'FIREBASE_FIRST_OPEN_DATE',
                splitable: true,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIREBASE_APP_STORE: {
            id: 'mopa.retention.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.retention.filter.firebase_app_store',
                    'FIREBASE_APP_STORE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.retention.filter.firebase_app_store'
                ].emitter,
        },
        FIREBASE_COUNTRY_EUROPEAN_UNION: {
            id: 'mopa.retention.filter.firebase_country_european_union',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.retention.filter.firebase_country_european_union',
                    'FIREBASE_COUNTRY_EUROPEAN_UNION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.retention.filter.firebase_country_european_union'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'mopa.retention.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.retention.filter.firebase_country_tier',
                    'FIREBASE_COUNTRY_TIER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.retention.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'mopa.retention.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.retention.filter.firebase_country',
                    'FIREBASE_COUNTRY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.retention.filter.firebase_country']
                    .emitter,
        },
        FIREBASE_COUNTRY_US_REGION: {
            id: 'mopa.retention.filter.firebase_country_us_region',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.retention.filter.firebase_country_us_region',
                    'FIREBASE_COUNTRY_US_REGION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.retention.filter.firebase_country_us_region'
                ].emitter,
        },
        ATTRIBUTION_TRAFFIC_SOURCE: {
            id: 'mopa.retention.filter.attribution_traffic_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.retention.filter.attribution_network_name',
                    'ATTRIBUTION_TRAFFIC_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.retention.filter.attribution_traffic_source'
                ].emitter,
        },
        FIREBASE_INSTALL_VERSION_GROUP_MINOR: {
            id: 'lnk.mopa.retention.filter.firebase_install_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'di.mopa.retention.filter.firebase_install_version_group_minor',
                    'FIREBASE_INSTALL_VERSION_GROUP_MINOR'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.retention.filter.firebase_install_version_group_minor'
                ].emitter,
        },
        // 'FIREBASE_INSTALL_VERSION':
        FIREBASE_INSTALL_VERSION: {
            id: 'lnk.mopa.retention.filter.firebase_install_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'di.mopa.retention.filter.firebase_install_version',
                    'FIREBASE_INSTALL_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.retention.filter.firebase_install_version'
                ].emitter,
        },
        // 'ETL_PAYER':
        ETL_PAYER: {
            id: 'mopa.retention.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.retention.filter.etl_payer',
                    'ETL_PAYER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.retention.filter.etl_payer']
                    .emitter,
        },
        ETL_REVENUE_BADGE: {
            id: 'mopa.retention.filter.etl_revenue_badge',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.retention.filter.etl_revenue_badge',
                    'ETL_REVENUE_BADGE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.retention.filter.etl_revenue_badge'
                ].emitter,
        },
        ATTRIBUTION_NETWORK_NAME: {
            id: 'mopa.retention.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.retention.filter.attribution_network_name',
                    'ATTRIBUTION_NETWORK_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.retention.filter.attribution_network_name'
                ].emitter,
        },
        ATTRIBUTION_CAMPAIGN_NAME: {
            id: 'mopa.retention.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.retention.filter.attribution_campaign_name',
                    'ATTRIBUTION_CAMPAIGN_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.retention.filter.attribution_campaign_name'
                ].emitter,
        },
    };

    override ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'MopaRetentionComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    ngAfterViewInit() {
        console.log({
            msg: 'MopaRetentionComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const firebaseAppStoreIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_APP_STORE',
                state: {
                    value: {
                        options: [
                            'Amazon Appstore',
                            'Google Play',
                            'Microsoft Store',
                            'iTunes',
                            'iTunes macOS',
                        ],
                        filterType: 'include',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const firebaseCountryTierIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_COUNTRY_TIER',
                state: {
                    value: {
                        options: ['Tier 1', 'Tier 2', 'Tier 3'],
                        filterType: 'include',
                    },
                    // filterType: 'include',
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_FIRST_OPEN_DATE',
                state: {
                    value: {
                        preset: 'last 365 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStorcd ho
            activityDateIC,
        ];

        initialContext.settings = {
            [QueryContextSettingID.RETENTION_MAX_DAY_NO]: '180',
            [QueryContextSettingID.RETENTION_SELECTED_DAY_NO]: ['1', '7', '30'],
        };

        return initialContext;
    }
}
